import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

// Unit capacity — the prepaid pool an org buys and spends on buildings.
//
// Uses the DEFAULT axios instance so these authenticated calls inherit the
// interceptors in axios-setup.ts (credentials, CSRF header, 401 -> refresh ->
// retry), like hierarchy-client.ts and for the same reason.

// What GET /subscriptions/allowance returns. This is THE shape the UI reads;
// nothing recomputes `remaining` from the other two.
export interface UnitAllowance {
  // Always true against a current server. There is no unmetered tier: an org
  // with no live subscription, or a legacy NULL quantity, is allowed ZERO units
  // rather than unlimited ones (backend migration 057 — it used to be the other
  // way round, and that was the free-units hole).
  capped: boolean;
  // Still `| null` in the type, not because the server sends null any more but
  // because a browser holding an older bundle may be talking to an older server
  // during a rollout. use-unit-allowance.ts is the ONE place that resolves that:
  // it falls back to Infinity only when `capped` is false, so a null from a new
  // server (which cannot happen) would read as 0, not as unlimited.
  units_allowed: number | null;
  units_used: number;
  units_remaining: number | null;
  // Whether THIS caller may raise the quantity. A property_manager creates units
  // but holds no billing permission, so they are told to ask the account owner
  // rather than shown a button that would 403.
  can_buy: boolean;
  // A complimentary (comped) account: there is nothing to buy, and can_buy is
  // false for everyone. Optional because an older server does not send it.
  comped?: boolean;
  // Archived (previously returned) units the org could bring back through the
  // restore flow. A count only — per-unit restorability (building active,
  // number free) comes from GET /units/returned. Optional because an older
  // server does not send it; absence must read as zero, never as unknown-many.
  units_returned?: number;
  buildings: BuildingAllocation[];
}

export interface BuildingAllocation {
  id: number;
  name: string;
  allocated: number;
  // Free 'UNASSIGNED' placeholder units. Outside billing, so reported apart from
  // `allocated` — the sum of `allocated` always equals units_used.
  placeholders: number;
}

export interface AllocateUnitsResult {
  building_id: number;
  requested: number;
  created: number;
  // Non-zero only when something raced onto the same unit numbers. Show it
  // rather than claiming the full count landed.
  skipped: number;
  units: { id: number; unit_number: string }[];
}

// Raised when the account is out of prepaid units. Carries the allowance the
// server computed, so the buy-more dialog opens with real numbers instead of
// having to re-fetch (and possibly re-derive) them.
export class UnitLimitError extends Error {
  readonly allowance: {
    capped: boolean;
    unit_quantity: number | null;
    used: number;
    remaining: number;
  } | null;

  constructor(message: string, allowance: UnitLimitError['allowance']) {
    super(message);
    this.name = 'UnitLimitError';
    this.allowance = allowance;
  }
}

// Raised when a building has no allocated units, so nobody can be placed in it.
// Distinct from UnitLimitError: the account may have plenty of units left, they
// just have not been allocated to THIS building. Buying more would not help;
// allocating would.
export class BuildingNotAllocatedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BuildingNotAllocatedError';
  }
}

// Turns the two documented allocation failures into typed errors and leaves
// everything else as-is. The server's message is always more specific than
// anything reconstructed here (it carries the real counts), so it is what the
// UI displays.
export const asUnitError = (error: unknown): unknown => {
  if (!axios.isAxiosError(error)) {
    return error;
  }

  const status = error.response?.status;
  const data = error.response?.data as
    | {
        message?: string;
        code?: string;
        allowance?: UnitLimitError['allowance'];
      }
    | undefined;

  if (status === 403 && data?.code === 'UNIT_LIMIT') {
    return new UnitLimitError(
      data.message || 'You have used all of your units.',
      data.allowance ?? null
    );
  }

  if (status === 409 && data?.code === 'BUILDING_NOT_ALLOCATED') {
    return new BuildingNotAllocatedError(
      data.message || 'This building has no units allocated yet.'
    );
  }

  return error;
};

export const getUnitAllowance = (): Promise<UnitAllowance> =>
  axios
    .get<UnitAllowance>(`${apiRoute}/subscriptions/allowance`)
    .then(r => r.data);

// Put `quantity` units into a building, atomically — the allowance is checked
// once for the whole batch, so this either lands entirely or creates nothing.
export const allocateUnits = (
  buildingId: number,
  quantity: number
): Promise<AllocateUnitsResult> =>
  axios
    .post<AllocateUnitsResult>(
      `${apiRoute}/buildings/${buildingId}/units/allocate`,
      { quantity }
    )
    .then(r => r.data)
    .catch(error => {
      throw asUnitError(error);
    });

// GET /subscriptions/units/preview — the server-side quote, and the ONLY source
// of money on this screen. Four figures that are not interchangeable:
//
//   rate_cents                 per unit per interval, straight off the plan
//   new_recurring_total_cents  rate x target — what the org pays every interval
//   amount_due_now_cents       what Stripe captures at this instant, often 0
//   proration_cents            the adjustment that lands on the NEXT invoice
//
// Showing any single one as "the price" is how a trialing org gets told $0.00
// while its monthly bill goes from $27 to $45. `null` means NOT KNOWN and must
// never be rendered as "$0.00".
//
// NOTHING IN THE FRONTEND MULTIPLIES A RATE BY A COUNT (owner decision D-12).
export interface UnitsPreview {
  current: number;
  used: number;
  target: number;
  delta: number;
  rate_cents: number;
  currency: string;
  interval: string;
  current_recurring_total_cents: number;
  new_recurring_total_cents: number;
  proration_cents: number | null;
  amount_due_now_cents: number | null;
  amount_due_now_error: string | null;
  // charge_now only: adjustments from EARLIER changes (a return's credit, say)
  // still waiting for the next regular invoice. Not part of today's charge.
  pending_adjustments_cents?: number | null;
  next_invoice_total_cents: number | null;
  next_invoice_at?: string | null;
  // The instant (unix SECONDS) Stripe priced this quote at. Sent back on the
  // commit so the charge is prorated from the moment the user was SHOWN, not
  // from whenever they got round to pressing Confirm. Null when the server
  // priced nothing (a refusal); optional for an older server.
  proration_date?: number | null;
  requires_card: boolean;
  // 'charge_now' (migration 105): an ANNUAL increase is invoiced and paid on
  // confirm, and amount_due_now_cents is exactly that invoice.
  mode: 'trial_anchor' | 'proration' | 'charge_now' | 'refuse';
  // On a PAYMENT_PENDING refusal: the page that completes the waiting payment.
  payment?: { hosted_invoice_url: string | null; amount_due_cents: number | null } | null;
  reason?: string | null;
  message?: string | null;
  plan_name?: string | null;
  // Present only on a NO_PAYMENT_METHOD refusal: what adding a card would
  // actually do. Absent means the refusal is NOT recoverable from this screen,
  // which is also what an older server sends — so its absence must disable the
  // purchase rather than silently enable a card form.
  recovery?: {
    available: boolean;
    // What Stripe captures the moment the card is confirmed. Zero whenever a
    // trial still stands between now and the first invoice.
    first_charge_cents: number;
    // Whether the org's OWN trial anchor survives. Never a fresh trial.
    trial_preserved: boolean;
    starts_at: string | null;
  } | null;
}

export const previewUnits = (quantity: number): Promise<UnitsPreview> =>
  axios
    .get<UnitsPreview>(`${apiRoute}/subscriptions/units/preview`, {
      params: { quantity },
    })
    .then(r => r.data);

// POST /subscriptions/payment-method — mint a SetupIntent so the shared card
// modal has something to confirm. NOTHING IS CHARGED by this call: a SetupIntent
// saves a card, it does not move money.
//
// This is D-13's mandatory recovery path. WP-12 made the unit allowance fail
// closed, so an org with no live Stripe subscription is capped at ZERO units;
// without a way to add a card that cap is permanent. Lobbero never holds the
// card — Stripe does — and the id this returns is proved server-side on the
// commit, never trusted as sent.
export interface UnitCardSetup {
  mode: 'setup';
  client_secret: string;
  setup_intent_id: string;
}

export const startUnitCardSetup = (): Promise<UnitCardSetup> =>
  axios
    .post<UnitCardSetup>(`${apiRoute}/subscriptions/payment-method`)
    .then(r => r.data);

// Raise the org's prepaid quantity to a new TOTAL (not a delta). The server
// applies the change at Stripe FIRST and mirrors the local cap only after
// Stripe confirms, so a dismissed modal, a declined card or a Stripe failure
// all leave the allowance exactly where it was.
//
// It does NOT "simply raise the cap on a card-less trial" any more. That path
// handed out units nobody could ever be invoiced for, because Stripe cancels a
// card-less trial at trial_end rather than converting it. The server now refuses
// with 409 `NO_PAYMENT_METHOD` — which the caller turns into the card flow above
// rather than a dead end.
//
// `setupIntentId` is the OPTIONAL proof that a card was just confirmed. It is
// retrieved from Stripe server-side, bound to this organisation and spent
// exactly once; posting it twice is refused with `PROOF_ALREADY_USED`, which is
// what stops a re-sent request buying the same units again.
//
// `quote` ties the commit to the preview the user confirmed:
//   expected_current  the `current` that preview was priced against. If the
//                     org's quantity has moved since (another tab, another
//                     admin), the server answers 409 QUANTITY_CHANGED and
//                     changes nothing — the quote on screen was for a
//                     different purchase.
//   proration_date    the instant that preview was priced at, so the charge
//                     matches the figure that was shown.
//
// Refusals, all 4xx/5xx with a `code`, and every one of them means the allowance
// was left exactly as it was: UNIT_BELOW_USED, NO_SUBSCRIPTION,
// NO_PAYMENT_METHOD, PROOF_NOT_CONFIRMED, PROOF_ALREADY_USED, PROOF_NOT_YOURS,
// PLAN_NOT_PURCHASABLE, LIVE_ROW_MISMATCH, STRIPE_SUBSCRIPTION_INCOMPLETE,
// STRIPE_UPDATE_UNCONFIRMED, SUBSCRIPTION_LINK_FAILED, STRIPE_NOT_CONFIGURED,
// QUANTITY_CHANGED (409).
export const buyUnits = (
  newTotal: number,
  setupIntentId?: string | null,
  quote?: { expected_current?: number | null; proration_date?: number | null }
): Promise<{
  units_allowed: number;
  units_used: number;
  units_remaining: number;
  subscription_created?: boolean;
  // What Stripe captured just now — an annual increase is invoiced at once.
  charged_now_cents?: number;
}> =>
  axios
    .post(`${apiRoute}/subscriptions/units`, {
      quantity: newTotal,
      ...(setupIntentId ? { setup_intent: setupIntentId } : {}),
      ...(quote && quote.expected_current != null
        ? { expected_current: quote.expected_current }
        : {}),
      ...(quote && quote.proration_date != null
        ? { proration_date: quote.proration_date }
        : {}),
    })
    .then(r => r.data);

// ---------------------------------------------------------------------------
// RETURNING AND RESTORING UNITS — the other direction of the pool.
// ---------------------------------------------------------------------------

// One archived (previously returned) unit, as GET /units/returned reports it.
// Restoration reactivates the ORIGINAL record — same id, same building — which
// is why the row carries identity, not just a label.
export interface ReturnedUnit {
  id: number;
  unit_number: string;
  name: string | null;
  kind: string;
  floor: string | null;
  building_id: number;
  building_name: string;
  // False when the unit's building was deactivated: reactivate the building
  // first (that path re-checks the whole building against the allowance).
  building_active: boolean;
  // True when the unit number was reused while this one was archived — the
  // twin has to be renamed or removed before this one can come back.
  number_taken: boolean;
  has_history: boolean;
  // building_active && !number_taken. Capacity is deliberately NOT part of
  // this answer — whether the org can afford n of them is the allowance's
  // question, asked at restore time.
  restorable: boolean;
}

export const getReturnedUnits = (): Promise<{
  count: number;
  units: ReturnedUnit[];
}> => axios.get(`${apiRoute}/units/returned`).then(r => r.data);

// POST /subscriptions/units/return/preview — the CREDIT a return is worth,
// quoted by Stripe's own proration engine before anything is committed.
// Read-only. Refusals reuse the return route's codes, plus
// CREDIT_PREVIEW_UNAVAILABLE (409) when Stripe has no upcoming invoice to
// quote against — a card-less trial, for instance. The quote is a courtesy:
// its absence must never block the return, so every caller treats a rejection
// as "no number to show", not as a failure.
//
// `unallocated` is the count of PAID-FOR-BUT-UNASSIGNED units in the quote —
// see returnUnits below for why the pool has two halves.
export const previewUnitReturn = (
  unitIds: number[],
  unallocated = 0
): Promise<{
  // rows + unallocated, the total the dialog is quoting for
  returnable: number;
  returnable_units?: number;
  returnable_unallocated?: number;
  new_quantity: number;
  credit_cents: number;
  currency: string;
  // Where the credit lands: it reduces the NEXT invoice — on an annual plan,
  // the renewal. Not a refund.
  interval?: string | null;
  credit_applies_at?: string | null;
}> =>
  axios
    .post(`${apiRoute}/subscriptions/units/return/preview`, {
      unit_ids: unitIds,
      ...(unallocated > 0 ? { unallocated } : {}),
    })
    .then(r => r.data);

// One occupant's destination when their unit is being returned. Every OPEN
// occupancy of every returned unit must carry exactly one of these — the
// server refuses the whole return otherwise (RESOLUTION_INCOMPLETE). The
// system never chooses for a resident.
export interface ReturnResolution {
  occupancy_id: string;
  action: 'move_out' | 'transfer';
  to_unit_id?: number;
}

// POST /subscriptions/units/return — give back units. The prepaid pool has TWO
// halves and this hands back either or both in ONE Stripe decrement:
//
//   unitIds      ALLOCATED units, named exactly. Archived (soft — rows, ids,
//                hierarchy and history survive) and restorable later.
//   unallocated  a COUNT of units that are paid for but were never assigned to
//                a building. They have no rows, so they cannot be named; the
//                server bounds the count by `units_remaining` at commit time.
//                This half used to be unreturnable, which is why an org that
//                had bought 150 units and allocated none was told it had
//                nothing to give back while paying for all 150.
//
// The Stripe quantity drops by the total and the unused time is credited on the
// next invoice. `resolutions` covers occupants of the NAMED units (move out /
// transfer), executed inside the same locked transaction as the archive — a
// failure moves nobody. Refusals, each with a `code` and none of them applied
// anything: UNITS_NOT_FOUND (404), UNIT_OCCUPIED, UNIT_NOT_RETURNABLE,
// UNALLOCATED_NOT_AVAILABLE, RESOLUTION_NOT_FOUND (404),
// RESOLUTION_INCOMPLETE, RESOLUTION_TARGET_INVALID, RETURN_BELOW_MINIMUM,
// NO_SUBSCRIPTION, NO_STRIPE_SUBSCRIPTION, LIVE_ROW_MISMATCH,
// STRIPE_SUBSCRIPTION_INCOMPLETE, STRIPE_UPDATE_UNCONFIRMED,
// STRIPE_NOT_CONFIGURED.
export const returnUnits = (
  unitIds: number[],
  resolutions?: ReturnResolution[],
  unallocated = 0
): Promise<{
  // rows archived + unallocated slots given back
  returned: number;
  requested: number;
  returned_units?: number;
  returned_unallocated?: number;
  units: { id: number; unit_number: string; building_id: number }[];
  units_allowed: number;
  units_used: number;
  units_remaining: number;
  stripe_quantity: number;
  moved_out?: number;
  transferred?: number;
}> =>
  axios
    .post(`${apiRoute}/subscriptions/units/return`, {
      unit_ids: unitIds,
      ...(resolutions && resolutions.length ? { resolutions } : {}),
      ...(unallocated > 0 ? { unallocated } : {}),
    })
    .then(r => r.data);

// POST /units/restore — bring previously returned units back AS THEMSELVES.
// Spends the allowance exactly like creating units does (the server checks the
// same mint guard, once for the whole batch), so restoring can never exceed
// what was purchased; over capacity the whole batch is refused with
// UNIT_LIMIT, which asUnitError turns into the buy-more dialog's typed error.
export const restoreUnits = (
  unitIds: number[]
): Promise<{
  restored: number;
  units: { id: number; unit_number: string; building_id: number }[];
}> =>
  axios
    .post(`${apiRoute}/units/restore`, { unit_ids: unitIds })
    .then(r => r.data)
    .catch(error => {
      throw asUnitError(error);
    });
